import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Table, 
  Button, 
  message, 
  Typography, 
  Space,
  Tag,
  Progress,
  Statistic,
  Alert,
  Spin
} from 'antd';
import { 
  Database, 
  Play, 
  Pause, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Clock,
  BarChart3,
  MapPin,
  Search
} from 'lucide-react';
import { db, appId } from '../firebase/firebaseConfig.jsx';
import { collection, getDocs, doc, getDoc, getDocFromServer } from 'firebase/firestore';
import { API_CONFIG } from '../config/apiConfig';

const { Title, Text } = Typography;

const RegionalPOIMonitorPage = () => {
  // State management
  const [loading, setLoading] = useState(false);
  const [regions, setRegions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [regionDataStatus, setRegionDataStatus] = useState({});
  const [regionLoading, setRegionLoading] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  // 배치 상태는 위에서 정의된 것 사용

  // 통계 데이터
  const [poiStats, setPoiStats] = useState({
    totalPOIs: 0,
    regionCount: 0,
    categoryCount: 0,
    lastUpdated: null
  });

  // 지역 데이터 상태 확인 (통계 캐시 우선 방식)
  const checkRegionDataStatus = useCallback(async (regionKey) => {
    try {
      console.log(`🔍 ${regionKey} 지역 POI 데이터 확인 시작 (통계 캐시 우선)...`);
      
      // 로딩 상태 설정
      setRegionLoading(prev => ({ ...prev, [regionKey]: true }));
      
      const safeRegionKey = regionKey.replace(/\//g, '_'); // seoul/gangnam -> seoul_gangnam
      
      // 1. 통계 캐시에서 먼저 로드 시도 (서버에서 최신 데이터 가져오기)
      try {
        const artifactsDocRef = doc(db, `artifacts/${appId}`);
        const adminCollectionRef = collection(artifactsDocRef, 'admin');
        const statsDocRef = doc(adminCollectionRef, 'regionalPOIStats');
        const regionStatsCollectionRef = collection(statsDocRef, 'regionStats');
        const regionStatsDocRef = doc(regionStatsCollectionRef, safeRegionKey);
        
        // 서버에서 최신 데이터 가져오기
        let regionStatsSnapshot;
        try {
          regionStatsSnapshot = await getDocFromServer(regionStatsDocRef);
        } catch (serverError) {
          console.warn(`⚠️ ${regionKey} 서버에서 통계 로드 실패, 일반 getDoc 사용:`, serverError.message);
          regionStatsSnapshot = await getDoc(regionStatsDocRef);
        }
        
        if (regionStatsSnapshot.exists()) {
          const stats = regionStatsSnapshot.data();
          console.log(`✅ [캐시에서 로드] ${regionKey} 지역 통계: ${stats.totalPOIs}개 POI`);
          console.log('📍 데이터 출처: 배치 작업으로 생성된 통계 캐시 (regionalPOIStats/regionStats)');
          
          // 상태 업데이트
          setRegionDataStatus(prev => ({
            ...prev,
            [regionKey]: {
              hasData: stats.hasData || stats.totalPOIs > 0,
              count: stats.totalPOIs || 0,
              categoryStats: stats.categoryStats || {}
            }
          }));
          
          return {
            hasData: stats.hasData || stats.totalPOIs > 0,
            count: stats.totalPOIs || 0,
            categoryStats: stats.categoryStats || {}
          };
        }
      } catch (cacheError) {
        console.warn(`⚠️ ${regionKey} 통계 캐시 로드 실패, 폴백 방식 사용:`, cacheError.message);
      }
      
      // 2. 폴백: 기존 방식 (Firebase 직접 조회)
      console.log(`🔄 ${regionKey} 폴백: Firebase 직접 조회`);
      
      const collectionName = `artifacts/${appId}/regional_poi_cache`;
      
      // 카테고리 목록 정의 (Frontend와 동일)
      const categories = [
        'restaurant', 'myanmar_restaurant', 'foreign_community', 'tourist_spots',
        'korean_education', 'hospital', 'bank', 'market', 'cafe', 'bar',
        'shopping_mall', 'pharmacy'
      ];
      
      // 지역 문서 존재 확인 (서브컬렉션으로 확인)
      let hasRegionData = false;
      
      // 각 카테고리에서 데이터가 있는지 확인
      for (const category of categories) {
        try {
          const categoryRef = collection(db, collectionName, safeRegionKey, category);
          const categorySnapshot = await getDocs(categoryRef);
          if (!categorySnapshot.empty) {
            hasRegionData = true;
            break;
          }
        } catch (error) {
          // 카테고리가 존재하지 않으면 무시
        }
      }
      
      if (!hasRegionData) {
        console.log(`⚠️ ${regionKey}: Firebase에서 지역 데이터가 존재하지 않음`);
        setRegionDataStatus(prev => ({
          ...prev,
          [regionKey]: { hasData: false, count: 0 }
        }));
        return { hasData: false, count: 0 };
      }
      
      // 카테고리별 데이터 조회
      let totalPOIs = 0;
      const categoryStats = {};
      
      for (const category of categories) {
        try {
          const categoryRef = collection(db, collectionName, safeRegionKey, category);
          const categorySnapshot = await getDocs(categoryRef);
          const poiCount = categorySnapshot.size;
          
          totalPOIs += poiCount;
          categoryStats[category] = poiCount;
          
          if (poiCount > 0) {
            console.log(`📊 ${regionKey} - ${category}: ${poiCount}개 POI`);
          }
        } catch (error) {
          console.error(`❌ ${regionKey} - ${category} 조회 실패:`, error);
          categoryStats[category] = 0;
        }
      }
      
      const hasAnyData = totalPOIs > 0;
      console.log(`✅ [폴백: 실시간 계산] ${regionKey} 지역: 총 ${totalPOIs}개 POI (${hasAnyData ? '데이터 있음' : '데이터 없음'})`);
      console.log('📍 데이터 출처: Firebase 직접 조회 (통계 캐시 없음)');
      
      // 상태 업데이트
      setRegionDataStatus(prev => ({
        ...prev,
        [regionKey]: { 
          hasData: hasAnyData, 
          count: totalPOIs, 
          categoryStats 
        }
      }));
      
      return { hasData: hasAnyData, count: totalPOIs, categoryStats };
      
    } catch (error) {
      console.error(`❌ ${regionKey} 지역 데이터 확인 실패:`, error);
      setRegionDataStatus(prev => ({
        ...prev,
        [regionKey]: { hasData: false, count: 0 }
      }));
      return { hasData: false, count: 0 };
    } finally {
      setRegionLoading(prev => ({ ...prev, [regionKey]: false }));
    }
  }, []);

  // 배치 상태 모니터링
  const [batchStatus, setBatchStatus] = useState({
    isRunning: false,
    currentRegion: null,
    currentCategory: null,
    progress: 0,
    totalCategories: 12,
    processedCategories: 0,
    startTime: null,
    estimatedTime: null
  });

  // interval 참조 저장 (cleanup을 위해)
  const monitorIntervalRef = useRef(null);

  // 배치 상태 실시간 조회
  const checkBatchStatus = useCallback(async () => {
    try {
      const apiBaseUrl = API_CONFIG.baseURL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';
      const response = await fetch(`${apiBaseUrl}/api/regional-poi/batch/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        mode: 'cors', // 🔥 CORS 모드 명시
        credentials: 'include' // 🔥 인증 정보 포함
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const status = data.data;
          setBatchStatus(prev => ({
            ...prev,
            isRunning: status.isRunning,
            currentRegion: status.currentRegion,
            progress: status.processedRegions / status.totalRegions * 100,
            startTime: status.startTime
          }));
          
          // 현재 처리 중인 카테고리 정보 (스케줄러에서 제공하는 경우)
          if (status.currentCategory) {
            setBatchStatus(prev => ({
              ...prev,
              currentCategory: status.currentCategory,
              processedCategories: status.processedCategories || 0
            }));
          }
        }
      }
    } catch (error) {
      console.error('❌ 배치 상태 조회 실패:', error);
    }
  }, []);

  // 지역별 배치 실행 (POI 생성/업데이트) - 실제 백엔드 상태 모니터링
  const runRegionBatch = useCallback(async (regionKey) => {
    try {
      setRegionLoading(prev => ({ ...prev, [regionKey]: true }));
      
      // 배치 시작 시간 설정
      const startTime = new Date().toISOString();
      setBatchStatus(prev => ({
        ...prev,
        isRunning: true,
        currentRegion: regionKey,
        startTime: startTime,
        progress: 0
      }));
      
      console.log(`🚀 ${regionKey} 지역 POI 배치 실행 시작 (데이터 업데이트)`);
      
      // 기존 interval이 있으면 정리
      if (monitorIntervalRef.current) {
        clearInterval(monitorIntervalRef.current);
        monitorIntervalRef.current = null;
      }
      
      const apiBaseUrl = API_CONFIG.baseURL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';
      const response = await fetch(`${apiBaseUrl}/api/regional-poi/batch/region`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ regionKey }),
        mode: 'cors', // 🔥 CORS 모드 명시
        credentials: 'include' // 🔥 인증 정보 포함
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log(`✅ ${regionKey} 지역 배치 시작됨:`, data);
        
        // 실제 Firebase 컬렉션 데이터 저장 모니터링 시작
        monitorIntervalRef.current = setInterval(async () => {
          try {
            // Firebase에서 실제 데이터 저장 확인
            const collectionName = `artifacts/${appId}/regional_poi_cache`;
            const safeRegionKey = regionKey.replace(/\//g, '_');
            
            console.log(`🔍 ${regionKey} Firebase 데이터 저장 상태 확인 중...`);
            
            // 카테고리별 데이터 저장 확인
            const categories = [
              'restaurant', 'myanmar_restaurant', 'foreign_community', 'tourist_spots',
              'korean_education', 'hospital', 'bank', 'market', 'cafe', 'bar',
              'shopping_mall', 'pharmacy'
            ];
            
            let totalPOIs = 0;
            let completedCategories = 0;
            const categoryStats = {};
            
            for (const category of categories) {
              try {
                const categoryRef = collection(db, collectionName, safeRegionKey, category);
                const categorySnapshot = await getDocs(categoryRef);
                const poiCount = categorySnapshot.size;
                
                totalPOIs += poiCount;
                categoryStats[category] = poiCount;
                
                if (poiCount > 0) {
                  completedCategories++;
                  console.log(`✅ ${regionKey} - ${category}: ${poiCount}개 POI 저장됨`);
                }
              } catch (error) {
                console.log(`ℹ️ ${regionKey} - ${category}: 아직 데이터 없음`);
              }
            }
            
            // 진행률 계산
            const progress = Math.min((completedCategories / 12) * 100, 95);
            const currentCategory = categories[completedCategories] || '완료 중...';
            
            console.log(`📊 ${regionKey} 배치 진행: ${completedCategories}/12 카테고리 완료 (${Math.round(progress)}%)`);
            
            // 배치 상태 업데이트
            setBatchStatus(prev => ({
              ...prev,
              currentCategory: currentCategory,
              progress: progress,
              processedCategories: completedCategories
            }));
            
            // 모든 카테고리가 완료되었는지 확인 (최소 1개 이상의 POI가 있어야 완료로 간주)
            if (completedCategories >= 12 && totalPOIs > 0) {
              console.log(`🎉 ${regionKey} 지역 배치 실제 완료 확인: ${totalPOIs}개 POI 저장됨`);
              
              if (monitorIntervalRef.current) {
                clearInterval(monitorIntervalRef.current);
                monitorIntervalRef.current = null;
              }
              
              // 배치 완료 상태 설정
              setBatchStatus(prev => ({
                ...prev,
                isRunning: false,
                currentRegion: null,
                currentCategory: null,
                progress: 100
              }));
              
              message.success(`${regionKey} 지역 POI 데이터 업데이트 완료! (${totalPOIs}개 POI, 12개 카테고리 처리됨)`);
              
              // 배치 완료 후 데이터 상태 확인
              setTimeout(() => {
                console.log(`🔍 ${regionKey} 지역 배치 후 데이터 상태 확인`);
                checkRegionDataStatus(regionKey);
              }, 2000);
              
              return;
            }
            
          } catch (error) {
            console.error('❌ Firebase 데이터 확인 실패:', error);
          }
        }, 3000); // 3초마다 Firebase 데이터 저장 상태 확인
        
        // 최대 대기 시간 설정 (10분 후 강제 완료 - 실제 데이터 저장 시간 고려)
        const timeoutId = setTimeout(() => {
          if (monitorIntervalRef.current) {
            clearInterval(monitorIntervalRef.current);
            monitorIntervalRef.current = null;
          }
          
          console.log(`⏰ ${regionKey} 지역 배치 최대 대기 시간 초과`);
          
          setBatchStatus(prev => ({
            ...prev,
            isRunning: false,
            currentRegion: null,
            currentCategory: null,
            progress: 100
          }));
          
          message.warning(`${regionKey} 지역 배치가 예상보다 오래 걸리고 있습니다. 데이터 상태를 확인해주세요.`);
          
          // 데이터 상태 확인
          setTimeout(() => {
            checkRegionDataStatus(regionKey);
          }, 2000);
        }, 600000); // 10분 후 강제 완료 (실제 데이터 저장 시간 고려)
        
        // cleanup 함수에 timeout ID 저장 (필요시)
        // timeoutId는 자동으로 정리되므로 별도 저장 불필요
        
      } else {
        const errorText = await response.text();
        console.error(`❌ ${regionKey} 지역 배치 실행 실패:`, errorText);
        
        // 배치 실패 상태 설정
        setBatchStatus(prev => ({
          ...prev,
          isRunning: false,
          currentRegion: null,
          currentCategory: null,
          progress: 0
        }));
        
        message.error(`${regionKey} 지역 배치 실행 실패: ${errorText}`);
      }
    } catch (error) {
      console.error(`❌ ${regionKey} 지역 배치 실행 실패:`, error);
      
      // 배치 실패 상태 설정
      setBatchStatus(prev => ({
        ...prev,
        isRunning: false,
        currentRegion: null,
        currentCategory: null,
        progress: 0
      }));
      
      message.error(`${regionKey} 지역 배치 실행 중 오류가 발생했습니다.`);
    } finally {
      setRegionLoading(prev => ({ ...prev, [regionKey]: false }));
    }
  }, [checkRegionDataStatus]);

  // 컴포넌트 언마운트 시 interval 정리
  useEffect(() => {
    return () => {
      if (monitorIntervalRef.current) {
        clearInterval(monitorIntervalRef.current);
        monitorIntervalRef.current = null;
      }
    };
  }, []);


  // 지역 및 카테고리 로드 (Firebase 직접 조회)
  const loadRegionsAndCategories = useCallback(async () => {
    try {
      setLoading(true);
      
      console.log('🔍 Firebase에서 지역 및 통계 데이터 로드 시작...');
      
      // Firebase에서 직접 지역 목록 조회
      const collectionName = `artifacts/${appId}/regional_poi_cache`;
      
      // 스케줄러에서 정의된 전체 지역 목록 사용 (229개 지역)
      const regionsList = [
        // 서울특별시 (25개 구)
        { key: 'seoul/gangnam', name: '서울특별시/강남구' }, { key: 'seoul/gangdong', name: '서울특별시/강동구' },
        { key: 'seoul/gangbuk', name: '서울특별시/강북구' }, { key: 'seoul/gangseo', name: '서울특별시/강서구' },
        { key: 'seoul/gwanak', name: '서울특별시/관악구' }, { key: 'seoul/gwangjin', name: '서울특별시/광진구' },
        { key: 'seoul/guro', name: '서울특별시/구로구' }, { key: 'seoul/geumcheon', name: '서울특별시/금천구' },
        { key: 'seoul/nowon', name: '서울특별시/노원구' }, { key: 'seoul/dobong', name: '서울특별시/도봉구' },
        { key: 'seoul/dongdaemun', name: '서울특별시/동대문구' }, { key: 'seoul/dongjak', name: '서울특별시/동작구' },
        { key: 'seoul/mapo', name: '서울특별시/마포구' }, { key: 'seoul/seodaemun', name: '서울특별시/서대문구' },
        { key: 'seoul/seocho', name: '서울특별시/서초구' }, { key: 'seoul/seongdong', name: '서울특별시/성동구' },
        { key: 'seoul/seongbuk', name: '서울특별시/성북구' }, { key: 'seoul/songpa', name: '서울특별시/송파구' },
        { key: 'seoul/yangcheon', name: '서울특별시/양천구' }, { key: 'seoul/yeongdeungpo', name: '서울특별시/영등포구' },
        { key: 'seoul/yongsan', name: '서울특별시/용산구' }, { key: 'seoul/eunpyeong', name: '서울특별시/은평구' },
        { key: 'seoul/jongno', name: '서울특별시/종로구' }, { key: 'seoul/jung', name: '서울특별시/중구' },
        { key: 'seoul/jungnang', name: '서울특별시/중랑구' },
        
        // 부산광역시 (16개 구/군)
        { key: 'busan/haeundae', name: '부산광역시/해운대구' }, { key: 'busan/nam', name: '부산광역시/남구' },
        { key: 'busan/dong', name: '부산광역시/동구' }, { key: 'busan/dongnae', name: '부산광역시/동래구' },
        { key: 'busan/busanjin', name: '부산광역시/부산진구' }, { key: 'busan/buk', name: '부산광역시/북구' },
        { key: 'busan/sasang', name: '부산광역시/사상구' }, { key: 'busan/saha', name: '부산광역시/사하구' },
        { key: 'busan/seo', name: '부산광역시/서구' }, { key: 'busan/suyeong', name: '부산광역시/수영구' },
        { key: 'busan/yeonje', name: '부산광역시/연제구' }, { key: 'busan/yeongdo', name: '부산광역시/영도구' },
        { key: 'busan/jung', name: '부산광역시/중구' }, { key: 'busan/gijang', name: '부산광역시/기장군' },
        { key: 'busan/gangseo', name: '부산광역시/강서구' }, { key: 'busan/namhae', name: '부산광역시/남해구' },
        
        // 대구광역시 (8개 구/군)
        { key: 'daegu/nam', name: '대구광역시/남구' }, { key: 'daegu/dong', name: '대구광역시/동구' },
        { key: 'daegu/buk', name: '대구광역시/북구' }, { key: 'daegu/seo', name: '대구광역시/서구' },
        { key: 'daegu/suseong', name: '대구광역시/수성구' }, { key: 'daegu/dalseo', name: '대구광역시/달서구' },
        { key: 'daegu/dalseong', name: '대구광역시/달성군' }, { key: 'daegu/jung', name: '대구광역시/중구' },
        
        // 인천광역시 (10개 구/군)
        { key: 'incheon/jung', name: '인천광역시/중구' }, { key: 'incheon/dong', name: '인천광역시/동구' },
        { key: 'incheon/michuhol', name: '인천광역시/미추홀구' }, { key: 'incheon/yeonsu', name: '인천광역시/연수구' },
        { key: 'incheon/namdong', name: '인천광역시/남동구' }, { key: 'incheon/bupyeong', name: '인천광역시/부평구' },
        { key: 'incheon/gyeyang', name: '인천광역시/계양구' }, { key: 'incheon/seo', name: '인천광역시/서구' },
        { key: 'incheon/ganghwa', name: '인천광역시/강화군' }, { key: 'incheon/ongjin', name: '인천광역시/옹진군' },
        
        // 광주광역시 (5개 구)
        { key: 'gwangju/dong', name: '광주광역시/동구' }, { key: 'gwangju/seo', name: '광주광역시/서구' },
        { key: 'gwangju/nam', name: '광주광역시/남구' }, { key: 'gwangju/buk', name: '광주광역시/북구' },
        { key: 'gwangju/gwangsan', name: '광주광역시/광산구' },
        
        // 대전광역시 (5개 구)
        { key: 'daejeon/dong', name: '대전광역시/동구' }, { key: 'daejeon/jung', name: '대전광역시/중구' },
        { key: 'daejeon/seo', name: '대전광역시/서구' }, { key: 'daejeon/yuseong', name: '대전광역시/유성구' },
        { key: 'daejeon/daedeok', name: '대전광역시/대덕구' },
        
        // 울산광역시 (5개 구/군)
        { key: 'ulsan/jung', name: '울산광역시/중구' }, { key: 'ulsan/nam', name: '울산광역시/남구' },
        { key: 'ulsan/dong', name: '울산광역시/동구' }, { key: 'ulsan/buk', name: '울산광역시/북구' },
        { key: 'ulsan/ulju', name: '울산광역시/울주군' },
        
        // 세종특별자치시 (1개)
        { key: 'sejong/sejong', name: '세종특별자치시/세종시' },
        
        // 경기도 (31개 시/군)
        { key: 'gyeonggi/suwon', name: '경기도/수원시' }, { key: 'gyeonggi/seongnam', name: '경기도/성남시' },
        { key: 'gyeonggi/anyang', name: '경기도/안양시' }, { key: 'gyeonggi/bucheon', name: '경기도/부천시' },
        { key: 'gyeonggi/goyang', name: '경기도/고양시' }, { key: 'gyeonggi/yongin', name: '경기도/용인시' },
        { key: 'gyeonggi/ansan', name: '경기도/안산시' }, { key: 'gyeonggi/hwaseong', name: '경기도/화성시' },
        { key: 'gyeonggi/uijeongbu', name: '경기도/의정부시' }, { key: 'gyeonggi/gimpo', name: '경기도/김포시' },
        { key: 'gyeonggi/pyeongtaek', name: '경기도/평택시' }, { key: 'gyeonggi/siheung', name: '경기도/시흥시' },
        { key: 'gyeonggi/gunpo', name: '경기도/군포시' }, { key: 'gyeonggi/osan', name: '경기도/오산시' },
        { key: 'gyeonggi/yeoju', name: '경기도/여주시' }, { key: 'gyeonggi/icheon', name: '경기도/이천시' },
        { key: 'gyeonggi/anseong', name: '경기도/안성시' }, { key: 'gyeonggi/gwangju', name: '경기도/광주시' },
        { key: 'gyeonggi/hanam', name: '경기도/하남시' }, { key: 'gyeonggi/yangju', name: '경기도/양주시' },
        { key: 'gyeonggi/dongducheon', name: '경기도/동두천시' }, { key: 'gyeonggi/gwangmyeong', name: '경기도/광명시' },
        { key: 'gyeonggi/guri', name: '경기도/구리시' }, { key: 'gyeonggi/namyangju', name: '경기도/남양주시' },
        { key: 'gyeonggi/yangpyeong', name: '경기도/양평군' }, { key: 'gyeonggi/gapyeong', name: '경기도/가평군' },
        { key: 'gyeonggi/yeoncheon', name: '경기도/연천군' }, { key: 'gyeonggi/pocheon', name: '경기도/포천시' },
        
        // 강원도 (18개 시/군)
        { key: 'gangwon/chuncheon', name: '강원도/춘천시' }, { key: 'gangwon/wonju', name: '강원도/원주시' },
        { key: 'gangwon/gangneung', name: '강원도/강릉시' }, { key: 'gangwon/donghae', name: '강원도/동해시' },
        { key: 'gangwon/taebaek', name: '강원도/태백시' }, { key: 'gangwon/sokcho', name: '강원도/속초시' },
        { key: 'gangwon/samcheok', name: '강원도/삼척시' }, { key: 'gangwon/hongcheon', name: '강원도/홍천군' },
        { key: 'gangwon/hoengseong', name: '강원도/횡성군' }, { key: 'gangwon/yeongwol', name: '강원도/영월군' },
        { key: 'gangwon/pyeongchang', name: '강원도/평창군' }, { key: 'gangwon/jeongseon', name: '강원도/정선군' },
        { key: 'gangwon/cheorwon', name: '강원도/철원군' }, { key: 'gangwon/hwacheon', name: '강원도/화천군' },
        { key: 'gangwon/yanggu', name: '강원도/양구군' }, { key: 'gangwon/inje', name: '강원도/인제군' },
        { key: 'gangwon/goseong', name: '강원도/고성군' }, { key: 'gangwon/yangyang', name: '강원도/양양군' },
        
        // 충청북도 (11개 시/군)
        { key: 'chungbuk/cheongju', name: '충청북도/청주시' }, { key: 'chungbuk/chungju', name: '충청북도/충주시' },
        { key: 'chungbuk/jecheon', name: '충청북도/제천시' }, { key: 'chungbuk/boeun', name: '충청북도/보은군' },
        { key: 'chungbuk/okcheon', name: '충청북도/옥천군' }, { key: 'chungbuk/yeongdong', name: '충청북도/영동군' },
        { key: 'chungbuk/jeungpyeong', name: '충청북도/증평군' }, { key: 'chungbuk/jincheon', name: '충청북도/진천군' },
        { key: 'chungbuk/goesan', name: '충청북도/괴산군' }, { key: 'chungbuk/eumseong', name: '충청북도/음성군' },
        { key: 'chungbuk/danyang', name: '충청북도/단양군' },
        
        // 충청남도 (15개 시/군)
        { key: 'chungnam/cheonan', name: '충청남도/천안시' }, { key: 'chungnam/gongju', name: '충청남도/공주시' },
        { key: 'chungnam/boryeong', name: '충청남도/보령시' }, { key: 'chungnam/asan', name: '충청남도/아산시' },
        { key: 'chungnam/seosan', name: '충청남도/서산시' }, { key: 'chungnam/nonsan', name: '충청남도/논산시' },
        { key: 'chungnam/gyeryong', name: '충청남도/계룡시' }, { key: 'chungnam/dangjin', name: '충청남도/당진시' },
        { key: 'chungnam/geumsan', name: '충청남도/금산군' }, { key: 'chungnam/buyeo', name: '충청남도/부여군' },
        { key: 'chungnam/seocheon', name: '충청남도/서천군' }, { key: 'chungnam/cheongyang', name: '충청남도/청양군' },
        { key: 'chungnam/hongseong', name: '충청남도/홍성군' }, { key: 'chungnam/yesan', name: '충청남도/예산군' },
        { key: 'chungnam/taean', name: '충청남도/태안군' },
        
        // 전라북도 (14개 시/군)
        { key: 'jeonbuk/jeonju', name: '전라북도/전주시' }, { key: 'jeonbuk/gunsan', name: '전라북도/군산시' },
        { key: 'jeonbuk/iksan', name: '전라북도/익산시' }, { key: 'jeonbuk/jeongeup', name: '전라북도/정읍시' },
        { key: 'jeonbuk/namwon', name: '전라북도/남원시' }, { key: 'jeonbuk/gimje', name: '전라북도/김제시' },
        { key: 'jeonbuk/wanju', name: '전라북도/완주군' }, { key: 'jeonbuk/jinan', name: '전라북도/진안군' },
        { key: 'jeonbuk/muju', name: '전라북도/무주군' }, { key: 'jeonbuk/jangsu', name: '전라북도/장수군' },
        { key: 'jeonbuk/imsil', name: '전라북도/임실군' }, { key: 'jeonbuk/sunchang', name: '전라북도/순창군' },
        { key: 'jeonbuk/gochang', name: '전라북도/고창군' }, { key: 'jeonbuk/buan', name: '전라북도/부안군' },
        
        // 전라남도 (22개 시/군)
        { key: 'jeonnam/mokpo', name: '전라남도/목포시' }, { key: 'jeonnam/yeosu', name: '전라남도/여수시' },
        { key: 'jeonnam/suncheon', name: '전라남도/순천시' }, { key: 'jeonnam/naju', name: '전라남도/나주시' },
        { key: 'jeonnam/gwangyang', name: '전라남도/광양시' }, { key: 'jeonnam/damyang', name: '전라남도/담양군' },
        { key: 'jeonnam/gokseong', name: '전라남도/곡성군' }, { key: 'jeonnam/gurye', name: '전라남도/구례군' },
        { key: 'jeonnam/goheung', name: '전라남도/고흥군' }, { key: 'jeonnam/boseong', name: '전라남도/보성군' },
        { key: 'jeonnam/hwasun', name: '전라남도/화순군' }, { key: 'jeonnam/jangheung', name: '전라남도/장흥군' },
        { key: 'jeonnam/gangjin', name: '전라남도/강진군' }, { key: 'jeonnam/haenam', name: '전라남도/해남군' },
        { key: 'jeonnam/yeongam', name: '전라남도/영암군' }, { key: 'jeonnam/muan', name: '전라남도/무안군' },
        { key: 'jeonnam/hampyeong', name: '전라남도/함평군' }, { key: 'jeonnam/yeonggwang', name: '전라남도/영광군' },
        { key: 'jeonnam/jangseong', name: '전라남도/장성군' }, { key: 'jeonnam/wando', name: '전라남도/완도군' },
        { key: 'jeonnam/jindo', name: '전라남도/진도군' }, { key: 'jeonnam/sinan', name: '전라남도/신안군' },
        
        // 경상북도 (23개 시/군)
        { key: 'gyeongbuk/andong', name: '경상북도/안동시' }, { key: 'gyeongbuk/gimcheon', name: '경상북도/김천시' },
        { key: 'gyeongbuk/gumi', name: '경상북도/구미시' }, { key: 'gyeongbuk/yeongju', name: '경상북도/영주시' },
        { key: 'gyeongbuk/sangju', name: '경상북도/상주시' }, { key: 'gyeongbuk/mungyeong', name: '경상북도/문경시' },
        { key: 'gyeongbuk/yeongcheon', name: '경상북도/영천시' }, { key: 'gyeongbuk/gyeongju', name: '경상북도/경주시' },
        { key: 'gyeongbuk/gimhae', name: '경상북도/김해시' }, { key: 'gyeongbuk/pohang', name: '경상북도/포항시' },
        { key: 'gyeongbuk/yeongdeok', name: '경상북도/영덕군' }, { key: 'gyeongbuk/cheongdo', name: '경상북도/청도군' },
        { key: 'gyeongbuk/goryeong', name: '경상북도/고령군' }, { key: 'gyeongbuk/seongju', name: '경상북도/성주군' },
        { key: 'gyeongbuk/chilgok', name: '경상북도/칠곡군' }, { key: 'gyeongbuk/yecheon', name: '경상북도/예천군' },
        { key: 'gyeongbuk/bonghwa', name: '경상북도/봉화군' }, { key: 'gyeongbuk/uljin', name: '경상북도/울진군' },
        { key: 'gyeongbuk/ulleung', name: '경상북도/울릉군' }, { key: 'gyeongbuk/yeongyang', name: '경상북도/영양군' },
        { key: 'gyeongbuk/cheongsong', name: '경상북도/청송군' },
        
        // 경상남도 (18개 시/군)
        { key: 'gyeongnam/changwon', name: '경상남도/창원시' }, { key: 'gyeongnam/jinju', name: '경상남도/진주시' },
        { key: 'gyeongnam/tongyeong', name: '경상남도/통영시' }, { key: 'gyeongnam/sacheon', name: '경상남도/사천시' },
        { key: 'gyeongnam/gimhae', name: '경상남도/김해시' }, { key: 'gyeongnam/miryang', name: '경상남도/밀양시' },
        { key: 'gyeongnam/geoje', name: '경상남도/거제시' }, { key: 'gyeongnam/yangsan', name: '경상남도/양산시' },
        { key: 'gyeongnam/uiryeong', name: '경상남도/의령군' }, { key: 'gyeongnam/haman', name: '경상남도/함안군' },
        { key: 'gyeongnam/changnyeong', name: '경상남도/창녕군' }, { key: 'gyeongnam/goseong', name: '경상남도/고성군' },
        { key: 'gyeongnam/namhae', name: '경상남도/남해군' }, { key: 'gyeongnam/hadong', name: '경상남도/하동군' },
        { key: 'gyeongnam/sancheong', name: '경상남도/산청군' }, { key: 'gyeongnam/hamyang', name: '경상남도/함양군' },
        { key: 'gyeongnam/geochang', name: '경상남도/거창군' }, { key: 'gyeongnam/hapcheon', name: '경상남도/합천군' },
        
        // 제주특별자치도 (2개 시)
        { key: 'jeju/jeju', name: '제주특별자치도/제주시' }, { key: 'jeju/seogwipo', name: '제주특별자치도/서귀포시' }
      ];
      
      setRegions(regionsList);
      console.log('📋 Firebase에서 지역 목록 로드:', regionsList.length, '개');
      
      // 카테고리 목록 (Frontend와 동일)
      const categoriesList = [
        { key: 'restaurant', name: 'Restaurant' },
        { key: 'myanmar_restaurant', name: 'Myanmar Restaurant' },
        { key: 'foreign_community', name: 'Foreign Community' },
        { key: 'tourist_spots', name: 'Tourist Spots' },
        { key: 'korean_education', name: 'Korean Education' },
        { key: 'hospital', name: 'Hospital' },
        { key: 'bank', name: 'Bank' },
        { key: 'market', name: 'Market' },
        { key: 'cafe', name: 'Cafe' },
        { key: 'bar', name: 'Bar' },
        { key: 'shopping_mall', name: 'Shopping Mall' },
        { key: 'pharmacy', name: 'Pharmacy' }
      ];
      setCategories(categoriesList);
      console.log('📋 카테고리 목록 설정:', categoriesList.length, '개');
      
      // 전체 POI 통계 로드 (통계 캐시 우선)
      let totalPOIs = 0;
      let regionCount = regionsList.length;
      let categoryCount = categoriesList.length;
      
      // 1. 통계 캐시에서 전체 통계 로드 시도
      try {
        const artifactsDocRef = doc(db, `artifacts/${appId}`);
        const adminCollectionRef = collection(artifactsDocRef, 'admin');
        const statsDocRef = doc(adminCollectionRef, 'regionalPOIStats');
        
        // 서버에서 최신 데이터 가져오기
        let statsSnapshot;
        try {
          statsSnapshot = await getDocFromServer(statsDocRef);
        } catch (serverError) {
          console.warn('⚠️ 서버에서 통계 로드 실패, 일반 getDoc 사용:', serverError.message);
          statsSnapshot = await getDoc(statsDocRef);
        }
        
        if (statsSnapshot.exists()) {
          const statsFromCache = statsSnapshot.data();
          totalPOIs = statsFromCache.totalPOIs || 0;
          regionCount = statsFromCache.totalRegions || regionsList.length;
          
          console.log('✅ [캐시에서 로드] 전체 POI 통계:', {
            totalPOIs,
            totalRegions: statsFromCache.totalRegions,
            regionsWithData: statsFromCache.regionsWithData,
            regionsWithoutData: statsFromCache.regionsWithoutData
          });
          console.log('📍 데이터 출처: 배치 작업으로 생성된 통계 캐시 (regionalPOIStats)');
        } else {
          console.log('⚠️ 통계 캐시 없음, 현재 페이지 지역만 계산');
          
          // 2. 폴백: 현재 페이지의 지역들만 조회 (성능 최적화)
          const startIndex = (currentPage - 1) * pageSize;
          const endIndex = startIndex + pageSize;
          const currentPageRegions = regionsList.slice(startIndex, endIndex);
          
          console.log(`📊 현재 페이지 ${currentPage}의 ${currentPageRegions.length}개 지역 통계 계산 중...`);
          
          for (const region of currentPageRegions) {
            const safeRegionKey = region.key.replace(/\//g, '_');
            
            for (const category of categoriesList) {
              try {
                const categoryRef = collection(db, collectionName, safeRegionKey, category.key);
                const categorySnapshot = await getDocs(categoryRef);
                totalPOIs += categorySnapshot.size;
              } catch (error) {
                // 카테고리가 존재하지 않으면 무시
                console.log(`ℹ️ ${region.key} - ${category.key}: 데이터 없음`);
              }
            }
          }
          
          console.log('📍 데이터 출처: 현재 페이지 지역만 실시간 계산 (통계 캐시 없음)');
        }
      } catch (statsError) {
        console.warn('⚠️ 통계 캐시 로드 실패, 현재 페이지 지역만 계산:', statsError.message);
        
        // 폴백: 현재 페이지의 지역들만 조회
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const currentPageRegions = regionsList.slice(startIndex, endIndex);
        
        for (const region of currentPageRegions) {
          const safeRegionKey = region.key.replace(/\//g, '_');
          
          for (const category of categoriesList) {
            try {
              const categoryRef = collection(db, collectionName, safeRegionKey, category.key);
              const categorySnapshot = await getDocs(categoryRef);
              totalPOIs += categorySnapshot.size;
            } catch (error) {
              // 무시
            }
          }
        }
      }
      
      setPoiStats({
        totalPOIs,
        regionCount,
        categoryCount,
        lastUpdated: new Date().toISOString()
      });
      
      console.log('📊 최종 POI 통계:', { totalPOIs, regionCount, categoryCount });
      
    } catch (error) {
      console.error('❌ Firebase 데이터 로드 실패:', error);
      message.error('데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  // 지역 데이터 상태 컴포넌트
  const RegionDataStatus = ({ regionKey }) => {
    const dataStatus = regionDataStatus[regionKey];
    const isLoading = regionLoading[regionKey];
    
    if (isLoading) {
      return (
        <div className="flex items-center">
          <div className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></div>
          <Text type="secondary">Checking...</Text>
        </div>
      );
    }
    
    if (!dataStatus || dataStatus.hasData === null) {
      return (
        <div className="flex items-center">
          <div className="w-2 h-2 bg-gray-500 rounded-full mr-2"></div>
          <Text type="secondary">Not Checked</Text>
        </div>
      );
    }
    
    if (dataStatus.hasData) {
      return (
        <div className="flex flex-col">
          <div className="flex items-center mb-1">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
            <Text type="success" strong style={{ fontSize: '14px' }}>
              {dataStatus.count} POIs
            </Text>
          </div>
          {dataStatus.categoryStats && (
            <div className="text-xs text-gray-500 ml-4 mt-1">
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {Object.entries(dataStatus.categoryStats)
                  .filter(([_, count]) => count > 0)
                  .map(([cat, count]) => (
                    <span key={cat} className="inline-flex items-center">
                      <span className="text-gray-600">{cat.replace('_', ' ')}:</span>
                      <span className="font-medium text-blue-600 ml-1">{count}</span>
                    </span>
                  ))}
              </div>
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div className="flex items-center">
        <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
        <Text type="danger">No Data</Text>
      </div>
    );
  };

  // 테이블 컬럼 정의
  const tableColumns = [
    {
      title: 'Region',
      dataIndex: 'name',
      key: 'name',
      width: 120, // 지역명은 짧으므로 공간 축소
      render: (text, record) => (
        <div>
          <Text strong style={{ fontSize: '13px' }}>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.key}
          </Text>
        </div>
      ),
    },
    {
      title: 'Total POIs',
      key: 'totalPOIs',
      width: 300, // POI 정보가 길므로 공간 확대
      render: (_, record) => <RegionDataStatus regionKey={record.key} />,
    },
    {
      title: 'Action',
      key: 'action',
      width: 120, // 액션 버튼은 적당한 크기
      render: (_, record) => {
        const isLoading = regionLoading[record.key];
        const isBatchRunning = batchStatus.isRunning && batchStatus.currentRegion === record.key;
        
        return (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<Play className="w-3 h-3" />}
              loading={isLoading}
              disabled={isLoading || isBatchRunning}
              onClick={() => runRegionBatch(record.key)}
              title={
                isLoading 
                  ? `POI 데이터 업데이트 중... (${batchStatus.currentCategory || '처리 중'})`
                  : "POI 데이터 업데이트 (기존 데이터 갱신)"
              }
            >
              {isLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  <span>처리 중...</span>
                </div>
              ) : (
                'Update POI'
              )}
            </Button>
          </Space>
        );
      },
    },
  ];

  // 페이지네이션 핸들러
  const handlePageChange = (page) => {
    console.log(`📄 페이지 변경: ${currentPage} → ${page}`);
    setCurrentPage(page);
    
    // 페이지 변경 시 해당 페이지의 지역들 상태 확인
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentPageRegions = regions.slice(startIndex, endIndex);
    
    console.log(`📄 페이지 ${page} 변경: ${currentPageRegions.length}개 지역 상태 확인`);
    
    // 현재 페이지의 지역들 상태 확인 (즉시 실행)
    currentPageRegions.forEach(region => {
      const currentStatus = regionDataStatus[region.key];
      if (!currentStatus || currentStatus.hasData === null) {
        console.log(`🔍 ${region.key} 지역 상태 확인 시작`);
        checkRegionDataStatus(region.key);
      } else {
        console.log(`✅ ${region.key} 지역 상태 이미 확인됨: ${currentStatus.hasData ? `${currentStatus.count} POIs` : 'No Data'}`);
      }
    });
  };

  // 초기 로드
  useEffect(() => {
    loadRegionsAndCategories();
  }, [loadRegionsAndCategories]);

  // 초기 로드 후 첫 페이지 지역 데이터 자동 조회 (중복 제거)
  useEffect(() => {
    if (regions.length > 0 && currentPage === 1) {
      console.log('🚀 초기 로드: 첫 페이지 지역 데이터 자동 조회 시작');
      
      const startIndex = 0;
      const endIndex = pageSize;
      const firstPageRegions = regions.slice(startIndex, endIndex);
      
      console.log(`📄 첫 페이지: ${firstPageRegions.length}개 지역 데이터 조회`);
      
      // 첫 페이지의 지역들 데이터 조회
      firstPageRegions.forEach(region => {
        console.log(`🔍 ${region.key} 지역 초기 데이터 조회 시작`);
        checkRegionDataStatus(region.key);
      });
    }
  }, [regions, checkRegionDataStatus]); // regions가 로드되면 실행

  // 현재 페이지의 지역들 상태 확인 (페이지 변경 시 자동 데이터 조회)
  useEffect(() => {
    if (regions.length > 0) {
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const currentPageRegions = regions.slice(startIndex, endIndex);
      
      console.log(`📄 페이지 ${currentPage}: ${currentPageRegions.length}개 지역 상태 확인 시작`);
      
      // 현재 페이지의 지역들 데이터 조회 (상태 확인 여부와 관계없이)
      currentPageRegions.forEach(region => {
        const currentStatus = regionDataStatus[region.key];
        if (!currentStatus || currentStatus.hasData === null) {
          console.log(`🔍 ${region.key} 지역 상태 확인 시작 (페이지 변경)`);
          checkRegionDataStatus(region.key);
        } else {
          console.log(`✅ ${region.key} 지역 상태 이미 확인됨: ${currentStatus.hasData ? `${currentStatus.count} POIs` : 'No Data'}`);
        }
      });
    }
  }, [regions, currentPage, checkRegionDataStatus]); // regions 의존성 추가

  return (
    <div className="p-6">
      <style jsx>{`
        .regional-poi-table .ant-table-tbody > tr > td {
          padding: 8px 12px;
          vertical-align: top;
        }
        .regional-poi-table .ant-table-thead > tr > th {
          padding: 12px;
          font-weight: 600;
        }
        .regional-poi-table .ant-table-tbody > tr:hover > td {
          background-color: #f5f5f5;
        }
      `}</style>
      
      <div className="mb-6">
        <Title level={2} className="mb-2">
          <MapPin className="w-6 h-6 inline mr-2" />
          Regional POI Monitor
        </Title>
        <Text type="secondary">
          지역별 POI 데이터 모니터링 및 관리
        </Text>
      </div>

      {/* 통계 카드 */}
      <Row gutter={[24, 24]} className="mb-6">
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total POIs"
              value={poiStats.totalPOIs}
              prefix={<Database className="w-4 h-4" />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Regions"
              value={poiStats.regionCount}
              prefix={<MapPin className="w-4 h-4" />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Categories"
              value={poiStats.categoryCount}
              prefix={<BarChart3 className="w-4 h-4" />}
            />
          </Card>
        </Col>
      </Row>

      {/* 배치 상태 */}
      {batchStatus.isRunning && (
        <Alert
          message={`🏢 ${batchStatus.currentRegion} 지역 POI 배치 실행 중`}
          description={
            <div>
              <div style={{ marginBottom: '8px' }}>
                <Text strong>📊 현재 처리 중: {batchStatus.currentCategory || '카테고리 처리 중...'}</Text>
              </div>
              <Progress 
                percent={Math.round(batchStatus.progress)} 
                status="active"
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
              <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                <span>⏱️ 시작 시간: {batchStatus.startTime ? new Date(batchStatus.startTime).toLocaleTimeString() : '알 수 없음'}</span>
                <span>📈 진행률: {Math.round(batchStatus.progress)}%</span>
              </div>
            </div>
          }
          type="info"
          showIcon
          className="mb-4"
        />
      )}

      {/* 액션 버튼 */}
      <Card className="mb-6">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Button
              icon={<RefreshCw className="w-4 h-4" />}
              onClick={loadRegionsAndCategories}
              loading={loading}
              className="w-full"
              size="large"
            >
              새로고침
            </Button>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Button
              icon={<Search className="w-4 h-4" />}
              onClick={async () => {
                try {
                  // 현재 페이지의 모든 지역 상태 확인
                  const startIndex = (currentPage - 1) * pageSize;
                  const endIndex = startIndex + pageSize;
                  const currentPageRegions = regions.slice(startIndex, endIndex);
                  
                  console.log(`🔍 현재 페이지 ${currentPage}의 ${currentPageRegions.length}개 지역 수동 확인 시작`);
                  message.loading(`현재 페이지 ${currentPage}의 ${currentPageRegions.length}개 지역 데이터를 확인 중...`, 0);
                  
                  // 모든 지역을 병렬로 확인
                  const promises = currentPageRegions.map(region => {
                    console.log(`🔍 ${region.key} 지역 수동 확인 시작`);
                    return checkRegionDataStatus(region.key);
                  });
                  
                  await Promise.all(promises);
                  
                  message.destroy(); // 로딩 메시지 제거
                  message.success(`현재 페이지 ${currentPage}의 ${currentPageRegions.length}개 지역 데이터 확인 완료!`);
                  
                } catch (error) {
                  console.error('❌ 현재 페이지 확인 실패:', error);
                  message.destroy(); // 로딩 메시지 제거
                  message.error('현재 페이지 데이터 확인 중 오류가 발생했습니다.');
                }
              }}
              className="w-full"
              size="large"
            >
              현재 페이지 확인
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 지역 테이블 */}
      <Card title="Available Regions">
        <Table
          dataSource={regions}
          columns={tableColumns}
          rowKey="key"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            onChange: handlePageChange,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} regions`
          }}
          loading={loading}
          size="small"
          scroll={{ x: 600 }} // 가로 스크롤 활성화
          className="regional-poi-table"
        />
      </Card>
    </div>
  );
};

export default RegionalPOIMonitorPage;
